import { useEffect, useRef } from 'react';
import type { Message } from './useLessonState';
import { fetchPhraseAudio, type LessonApi } from '../utils/stepHandlers';

interface LadderAudioInput {
  messages: Message[];
  setMessages: (update: (prev: Message[]) => Message[]) => void;
  api: LessonApi;
  signal?: AbortSignal;
}

/**
 * Fetches the clip a help ladder offers (BR-051): the newest message's `ladder.audioFormId`, once per message.
 * A clip that cannot be fetched leaves the meaning and cue in place without a player.
 */
export const useLadderAudio = ({
  messages,
  setMessages,
  api,
  signal,
}: LadderAudioInput): void => {
  const requested = useRef(new Set<string>());
  const latest = messages.length > 0 ? messages[messages.length - 1] : null;
  const formId = latest?.ladder?.audioFormId ?? null;
  const hasClip = !!latest?.ladder?.audioUrls;
  const id = latest?.id ?? null;

  useEffect(() => {
    if (!id || !formId || hasClip || requested.current.has(id)) return;
    requested.current.add(id);
    void fetchPhraseAudio(formId, api, signal)
      .then((audioUrls) =>
        setMessages((prev) =>
          prev.map((m) =>
            m.id === id && m.ladder
              ? { ...m, ladder: { ...m.ladder, audioUrls } }
              : m
          )
        )
      )
      .catch((error: unknown) => {
        console.error('[useLadderAudio] Clip unavailable:', error);
      });
  }, [id, formId, hasClip, api, signal, setMessages]);
};
